import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import {
  QueryFilters,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { API } from 'aws-amplify';
import React from 'react';
import isEmail from 'validator/lib/isEmail';
import { AgentProfileData, GHLSearchData } from '../../../types';
import { getErrorMessage } from '../../../utils/error-message';

export function GHLCreate(props: {
  queryKey: QueryFilters['queryKey'];
  profile: AgentProfileData | null;
}) {
  // State
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [Email, setEmail] = React.useState('');
  const [Fname, setFname] = React.useState('');
  const [Lname, setLname] = React.useState('');
  // Hooks
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (props.profile) {
      setEmail(props.profile.Email || '');
      setFname(props.profile.Fname || '');
      setLname(props.profile.Lname || '');
    }
  }, [props.profile]);

  // Mutation - Create an account in Go High Level.
  const mutation = useMutation({
    mutationFn: async () => {
      await API.post('ContractingAPI', '/go-high-level/create', {
        body: {
          Email,
          Fname,
          Lname,
        },
      });
    },
    onSuccess: async () => {
      // Update /search for account in Go High Level
      if (props.queryKey) {
        queryClient.setQueryData<GHLSearchData>(props.queryKey, (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              firstName: Fname,
              lastName: Lname,
              email: Email,
              tags: ['active', 'email_blast'],
            };
          } else {
            return {
              id: '',
              firstName: Fname,
              lastName: Lname,
              source: '',
              phone: '',
              email: Email,
              country: '',
              state: '',
              postalCode: '',
              city: '',
              tags: ['active', 'email_blast'],
            };
          }
        });
      }

      // Close form
      setDialogOpen(false);
    },
  });

  const handleClose = () => {
    if (!mutation.isLoading) {
      setDialogOpen(false);
      mutation.reset();
    }
  };

  const submitDisabled = !Email || !isEmail(Email) || !Fname || !Lname;

  return (
    <React.Fragment>
      <Button
        size="small"
        disableElevation
        variant="contained"
        endIcon={<ArrowForwardIcon fontSize="small" />}
        onClick={() => setDialogOpen(true)}
      >
        Start
      </Button>

      <Dialog open={dialogOpen} onClose={handleClose}>
        <DialogTitle>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>Join Go High Level</Box>

            <IconButton
              disabled={mutation.isLoading}
              size="small"
              aria-label="close"
              onClick={handleClose}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            <DialogContentText>
              HighLevel is the first-ever all-in-one platform that will give you
              the tools, support and resources you need to succeed and crush
              your marketing goals.
            </DialogContentText>

            <TextField
              id="ghl-create-Email"
              autoFocus
              required
              fullWidth
              variant="outlined"
              label="Email Address"
              type="email"
              value={Email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <TextField
              id="ghl-create-Fname"
              required
              fullWidth
              variant="outlined"
              label="First Name"
              value={Fname}
              onChange={(event) => setFname(event.target.value)}
            />
            <TextField
              id="ghl-create-Lname"
              required
              fullWidth
              variant="outlined"
              label="Last Name"
              value={Lname}
              onChange={(event) => setLname(event.target.value)}
            />
          </Stack>
        </DialogContent>

        {mutation.isError ? (
          <Alert
            onClose={() => mutation.reset()}
            severity="error"
            sx={{ borderRadius: 0 }}
          >
            <strong style={{ marginRight: 10 }}>An error has occurred!</strong>
            <small>
              {getErrorMessage({
                error: mutation.error,
                fallback: 'Please try again.',
              })}
            </small>
          </Alert>
        ) : null}

        {mutation.isLoading ? <LinearProgress /> : null}

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            disabled={mutation.isLoading || submitDisabled}
            onClick={() => {
              mutation.mutate();
            }}
          >
            {mutation.isLoading ? 'Joining...' : 'Join now'}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
